import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildTioAuthHeaders } from '../utils/auth-headers.ts';

/**
 * Admin record inspection uses /eligibility-files/<jobLogId>/records/<category> and /meta-diff; the
 * path uses eligibility-files rather than the job-log model type. AbortSignal passes through to
 * request cancellation.
 * The CSV export route still needs API confirmation: it was absent from the inspected route
 * configuration despite an existing frontend caller.
 */

const eligibilityFilesUrlOptions: QueryUrlOptions = {
  identifier: { type: 'eligibility-files' },
  op: 'query',
  resourcePath: 'eligibility-files',
};

function eligibilityFilesUrl(path?: string): string {
  const base = buildBaseURL(eligibilityFilesUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/**
 * GET /eligibility-files/{jobLogId}/records/{category}?page={page}&per_page={perPage}
 *
 * Mirrors `EligibilityFileJobLogAdapter#fetchRecords`, which built the URL via
 * `this.buildURL('eligibility-files', jobLogId) + '/records/' + category +
 * '?page=' + page + '&per_page=' + perPage'` and sent
 * `this.ajax(url, 'GET', { signal })`. The `page` / `per_page` query keys are
 * appended UNCONDITIONALLY in that order (the adapter always interpolated both),
 * preserved exactly here. The optional `AbortSignal` rides `RequestInfo.signal`.
 */
export function fetchEligibilityRecordsRequest(
  jobLogId: string,
  category: string,
  page: number,
  perPage: number,
  signal?: AbortSignal
): RequestInfo {
  return {
    url: eligibilityFilesUrl(`${jobLogId}/records/${category}?page=${page}&per_page=${perPage}`),
    method: 'GET',
    signal,
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /eligibility-files/{jobLogId}/meta-diff
 *
 * Mirrors `EligibilityFileJobLogAdapter#fetchMetaDiff`, which built the URL via
 * `this.buildURL('eligibility-files', jobLogId) + '/meta-diff'` and sent
 * `this.ajax(url, 'GET', { signal })`. The optional `AbortSignal` rides
 * `RequestInfo.signal`.
 */
export function fetchEligibilityMetaDiffRequest(
  jobLogId: string,
  signal?: AbortSignal
): RequestInfo {
  return {
    url: eligibilityFilesUrl(`${jobLogId}/meta-diff`),
    method: 'GET',
    signal,
    cacheOptions: { [SkipCache]: true },
  };
}

/* -------------------------------------------------------------------------
 * BLOB download — raw fetch helper (NOT store.request).
 *
 * `records/{category}/export` streams a `text/csv` body, bypassing JSON:API
 * content negotiation, so it cannot ride the `store.request` builder pipeline.
 * It mirrors the established raw-fetch download pattern (`search.ts`,
 * `accounting-downloads.ts`): `buildTioAuthHeaders` reproduces the legacy
 * ApplicationAdapter `headers` bundle (x-api-key from the caller's per-app
 * `ENV.apiKey`, x-auth-key / tio-auth-token / Time-Zone / X-Request-Id from
 * auth-context), and the legacy `Accept: text/csv` override is preserved.
 * ------------------------------------------------------------------------- */

/**
 * `{host}/eligibility-files/{jobLogId}/records/{category}/export[?fields[]=...]`
 *
 * Mirrors the adapter's
 * `this.buildURL('eligibility-files', jobLogId) + '/records/' + category +
 * '/export' + (query ? '?' + query : '')`, where `query` is built from a
 * `URLSearchParams` that appends one `fields[]=<field>` per chosen field. The
 * `URLSearchParams.toString()` encoder + the omit-the-`?`-when-empty behaviour
 * are reproduced exactly so the URL is byte-identical. Exported so the
 * request-snapshot test + 3.2c-ii call site can assert/reuse it.
 */
export function eligibilityRecordsExportUrl(
  jobLogId: string,
  category: string,
  fields: string[]
): string {
  const params = new URLSearchParams();
  for (const field of fields) {
    params.append('fields[]', field);
  }
  const query = params.toString();
  return eligibilityFilesUrl(`${jobLogId}/records/${category}/export${query ? `?${query}` : ''}`);
}

/**
 * GET /eligibility-files/{jobLogId}/records/{category}/export?fields[]=... — fetch
 * a category of eligibility-file records as a CSV stream. Replaces
 * `EligibilityFileJobLogAdapter#exportRecords` minus the filename/`downloadFile`
 * trigger: that method derived the filename from `Content-Disposition` and called
 * `downloadFile`. The adapter's tio-common `downloadFile` import is NOT available
 * to tio-data (ADR-001: owner-free / tio-common-free), so — like the other
 * accounting/search blob helpers — this returns the raw `Response` and the 3.2c-ii
 * call site keeps the `Content-Disposition` filename parse + the `.ok` guard +
 * `downloadFile`. (The single call site, `eligibility-files/record-details-modal.gts`,
 * already runs that logic; moving the trigger keeps the call site byte-identical.)
 *
 * Headers match the adapter EXACTLY: the full `headers` bundle with
 * `Content-Type: application/vnd.api+json` (the adapter spread `...this.headers`,
 * whose default Content-Type is vnd.api+json) and `Accept: text/csv` (the
 * adapter's explicit override).
 *
 * @param jobLogId the eligibility-file job-log id
 * @param category the record category (created / updated / deactivated / reactivated)
 * @param fields   the chosen `fields[]` to include in the CSV
 * @param apiKey   the per-app `ENV.apiKey` (tio-data is owner-free — see auth-headers)
 */
export function exportEligibilityRecords(
  jobLogId: string,
  category: string,
  fields: string[],
  { apiKey }: { apiKey: string }
): Promise<Response> {
  return fetch(eligibilityRecordsExportUrl(jobLogId, category, fields), {
    method: 'GET',
    headers: buildTioAuthHeaders({
      apiKey,
      contentType: 'application/vnd.api+json',
      accept: 'text/csv',
    }),
  });
}
